/**
 * Water (P1-T08): bay surface with animated waves, foam at the scalloped
 * shoreline and sky reflection; fountain jets + splash rings at plaza
 * fountains. Ported from the reference prototype's water shader, with the
 * shoreline distance computed in-shader from the same scallop function as
 * slice_layout.py (so there is no seam against the promenade deck).
 *
 * Reflections: analytic sky term (horizon fog + zenith + sun glint) for all
 * tiers at P1; SSR/probe variants arrive with P1-T09 lighting work.
 */
import type { Scene, ShaderMaterial } from 'babylonjs';
import type { Tier } from '@kota-nova/shared';

const WATER_Y = -0.05; // deck top is 0.13; water laps just below it
// must match slice_layout.py shore_z()
const BAY_EDGE_Z = -150.0;
const BAY_SCALLOP = 8.0;

const WATER_VERT = /* glsl */ `
precision highp float;
attribute vec3 position;
uniform mat4 world; uniform mat4 viewProjection;
varying vec3 vPos;
void main(void) {
  vec4 wp = world * vec4(position, 1.0);
  vPos = wp.xyz;
  gl_Position = viewProjection * wp;
}`;

const WATER_FRAG = /* glsl */ `
precision highp float;
varying vec3 vPos;
uniform vec3 camPos; uniform vec3 sunDir; uniform vec3 sunColor;
uniform float time; uniform vec3 deep; uniform vec3 shallow;
uniform vec3 fogColor; uniform float fogDensity;
uniform vec2 flow; uniform float rain; uniform float night;
uniform float overcast;
uniform sampler2D nmap; uniform sampler2D foamTex;

// slice_layout.py: shore_z(x) = -150 + 8*sin(0.015x) + 3*sin(0.043x+1.7)
float shoreZ(float x) {
  return ${BAY_EDGE_Z.toFixed(1)} + ${BAY_SCALLOP.toFixed(1)} * sin(x * 0.015)
       + 3.0 * sin(x * 0.043 + 1.7);
}

// analytic sky along a reflection ray (matches the sky dome's mood)
vec3 skyAt(vec3 d) {
  float h = clamp(d.y, 0.0, 1.0);
  vec3 zen = mix(vec3(0.02, 0.03, 0.06), vec3(0.25, 0.45, 0.75), clamp(1.0 - night, 0.0, 1.0));
  vec3 hor = mix(vec3(0.05, 0.06, 0.09), fogColor, 0.75);
  vec3 c = mix(hor, zen, pow(h, 0.6));
  float sd = max(dot(d, sunDir), 0.0);
  c += sunColor * pow(sd, 40.0) * (1.0 - overcast * 0.8);
  return c;
}

void main(void) {
  vec2 uv = vPos.xz;
  vec3 n1 = texture2D(nmap, uv * 0.027 + flow * time * 0.03 + vec2(time * 0.010, time * 0.006)).xyz * 2.0 - 1.0;
  vec3 n2 = texture2D(nmap, uv * 0.061 - vec2(time * 0.016, -time * 0.012)).xyz * 2.0 - 1.0;
  vec3 n3 = texture2D(nmap, uv * 0.33 + vec2(time * 0.07, time * 0.05)).xyz * 2.0 - 1.0;
  float amp = 1.0;
  vec3 nn = normalize(vec3((n1.x + n2.x) * amp + n3.x * (0.25 + rain * 1.2), 5.0, (n1.y + n2.y) * amp + n3.y * (0.25 + rain * 1.2)));
  vec3 V = normalize(camPos - vPos);
  float dist = length(camPos - vPos);
  nn = normalize(mix(nn, vec3(0.0, 1.0, 0.0), clamp(dist / 1200.0, 0.0, 0.85)));

  vec3 R = reflect(-V, nn); R.y = abs(R.y) + 0.02;
  vec3 refl = skyAt(normalize(R)) * mix(0.35, 1.0, clamp(1.0 - night, 0.0, 1.0)) * (1.0 - overcast * 0.35);
  float fres = 0.025 + 0.975 * pow(1.0 - max(dot(nn, V), 0.0), 5.0);

  float depth = clamp(vPos.z - shoreZ(vPos.x), 0.0, 300.0); // 0 at the shore
  vec3 body = mix(shallow, deep, clamp(depth / 220.0, 0.0, 1.0));
  body *= (0.18 + 0.82 * (1.0 - night * 0.9));

  vec3 col = mix(body, refl, fres);
  vec3 H = normalize(sunDir + V);
  float spec = pow(max(dot(nn, H), 0.0), mix(700.0, 2400.0, night)) * mix(7.0, 1.2, night) * (1.0 - overcast * 0.7);
  col += sunColor * spec;

  // foam: noise band hugging the scalloped shore, breathing with the swell
  float shoreD = vPos.z - shoreZ(vPos.x);           // <0 = over land edge
  float band = 1.0 - smoothstep(0.0, 3.2, abs(shoreD));
  float wob = 0.5 + 0.5 * sin(time * 1.1 + vPos.x * 0.35 + shoreD * 1.4);
  float fn = texture2D(foamTex, uv * 0.35 + vec2(time * 0.02, time * 0.05)).r;
  float foam = band * smoothstep(0.45 - 0.25 * wob, 0.9, fn) * (1.0 - night * 0.55);
  col = mix(col, vec3(0.86, 0.9, 0.92) * (0.35 + 0.65 * (1.0 - night)), clamp(foam, 0.0, 0.9));
  // rain pockmarks: fine sparkle everywhere
  col += rain * 0.05 * step(0.985, fn);

  float f = 1.0 - exp(-pow(fogDensity * dist, 2.0));
  col = mix(col, fogColor, f);
  gl_FragColor = vec4(col, 1.0);
}`;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface WaterSkySource {
  sunDir: Vec3;
  sunColor: Vec3;
  night: number;
  overcast: number;
  rain: number;
}

function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/** tileable wave-height field -> tangent-space normals (prototype recipe) */
function waterNormalTexture(): HTMLCanvasElement {
  const N = 256;
  const c = canvas(N, N);
  const g = c.getContext('2d');
  if (!g) return c;
  const img = g.createImageData(N, N);
  const d = img.data;
  let seed = 77;
  const rnd = (): number => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  const waves: { a: number; b: number; p: number; amp: number }[] = [];
  for (let k = 0; k < 14; k++) {
    waves.push({
      a: Math.round((rnd() - 0.5) * 14) || 1,
      b: Math.round((rnd() - 0.5) * 14),
      p: rnd() * 6.28,
      amp: 0.3 + rnd() * 0.7,
    });
  }
  const height = new Float32Array(N * N);
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      let v = 0;
      for (const w of waves)
        v += (Math.sin(((w.a * x + w.b * y) / N) * 6.2832 + w.p) * w.amp) / Math.hypot(w.a, w.b || 1);
      height[y * N + x] = v;
    }
  const strength = 3.5;
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      const g00 = height[y * N + ((x + N - 1) % N)] ?? 0;
      const g10 = height[y * N + ((x + 1) % N)] ?? 0;
      const g01 = height[((y + N - 1) % N) * N + x] ?? 0;
      const g11 = height[((y + 1) % N) * N + x] ?? 0;
      const nx = (g00 - g10) * strength;
      const ny = (g01 - g11) * strength;
      const len = Math.hypot(nx, ny, 1);
      const i = (y * N + x) * 4;
      d[i] = ((nx / len) * 0.5 + 0.5) * 255;
      d[i + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      d[i + 2] = ((1 / len) * 0.5 + 0.5) * 255;
      d[i + 3] = 255;
    }
  g.putImageData(img, 0, 0);
  return c;
}

function foamNoiseTexture(): HTMLCanvasElement {
  const N = 128;
  const c = canvas(N, N);
  const g = c.getContext('2d');
  if (!g) return c;
  const img = g.createImageData(N, N);
  const d = img.data;
  // layered value noise, tileable via wrapped lattice
  let seed = 5;
  const rnd = (): number => {
    seed = (seed * 48271) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  const lat = (n: number): Float32Array => {
    const a = new Float32Array(n * n);
    for (let i = 0; i < a.length; i++) a[i] = rnd();
    return a;
  };
  const smooth = (t: number): number => t * t * (3 - 2 * t);
  const layer = (n: number, arr: Float32Array, x: number, y: number): number => {
    const fx = (x / 1) * n;
    const fy = (y / 1) * n;
    const x0 = Math.floor(fx) % n;
    const y0 = Math.floor(fy) % n;
    const tx = smooth(fx - Math.floor(fx));
    const ty = smooth(fy - Math.floor(fy));
    const a00 = arr[y0 * n + x0] ?? 0;
    const a10 = arr[y0 * n + ((x0 + 1) % n)] ?? 0;
    const a01 = arr[((y0 + 1) % n) * n + x0] ?? 0;
    const a11 = arr[((y0 + 1) % n) * n + ((x0 + 1) % n)] ?? 0;
    return a00 + (a10 - a00) * tx + (a01 - a00) * ty + (a00 - a10 - a01 + a11) * tx * ty;
  };
  const l1 = lat(8);
  const l2 = lat(16);
  const l3 = lat(32);
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      const v =
        0.5 * layer(8, l1, x / N, y / N) +
        0.3 * layer(16, l2, x / N, y / N) +
        0.2 * layer(32, l3, x / N, y / N);
      const i = (y * N + x) * 4;
      d[i] = d[i + 1] = d[i + 2] = Math.min(255, v * 320);
      d[i + 3] = 255;
    }
  g.putImageData(img, 0, 0);
  return c;
}

export class WaterField {
  private readonly mat: ShaderMaterial;
  private readonly B: typeof import('babylonjs');
  private readonly scene: Scene;

  constructor(babylon: typeof import('babylonjs'), scene: Scene, tier: Tier) {
    this.B = babylon;
    this.scene = scene;
    babylon.Effect.ShadersStore.knWaterVertexShader = WATER_VERT;
    babylon.Effect.ShadersStore.knWaterFragmentShader = WATER_FRAG;
    const m = new babylon.ShaderMaterial(
      'waterMat',
      scene,
      { vertex: 'knWater', fragment: 'knWater' },
      {
        attributes: ['position'],
        uniforms: [
          'world',
          'viewProjection',
          'camPos',
          'sunDir',
          'sunColor',
          'time',
          'deep',
          'shallow',
          'fogColor',
          'fogDensity',
          'flow',
          'rain',
          'night',
          'overcast',
        ],
        samplers: ['nmap', 'foamTex'],
      },
    );
    const B = babylon;
    const nTex = new B.DynamicTexture('waterN', { width: 256, height: 256 }, scene, true);
    const ctxN = nTex.getContext();
    ctxN.drawImage(waterNormalTexture(), 0, 0);
    nTex.update();
    nTex.wrapU = B.Texture.WRAP_ADDRESSMODE;
    nTex.wrapV = B.Texture.WRAP_ADDRESSMODE;
    m.setTexture('nmap', nTex);
    const foamTex = new B.DynamicTexture('foamN', { width: 128, height: 128 }, scene, true);
    const ctxF = foamTex.getContext();
    ctxF.drawImage(foamNoiseTexture(), 0, 0);
    foamTex.update();
    foamTex.wrapU = B.Texture.WRAP_ADDRESSMODE;
    foamTex.wrapV = B.Texture.WRAP_ADDRESSMODE;
    m.setTexture('foamTex', foamTex);

    m.setColor3('deep', new B.Color3(0.012, 0.05, 0.065));
    m.setColor3('shallow', new B.Color3(0.03, 0.12, 0.13));
    m.setVector2('flow', new B.Vector2(0, -0.5));
    m.backFaceCulling = false;

    // bay sheet: north edge at -138 covers every scallop maximum (shore z
    // range ≈ -161..-139); where the scallop leaves land, the deck (y 0.13)
    // hides the water (y -0.05) beneath it -> no seam, no void.
    const w = B.MeshBuilder.CreateGround('bay_water', { width: 3200, height: 2000, subdivisions: 1 }, scene);
    w.position.set(0, WATER_Y, -138 - 1000);
    w.material = m;
    w.isPickable = false;
    w.freezeWorldMatrix();
    // cover the gap under the deck lip a few metres inland of the scallop max
    // (shore z max = -150+8+3=-139; deck starts at -150..-128 cells; sheet
    // top at -150 sits under the deck edge -> no seam)
    this.mat = m;
    void tier;
  }

  /** per-frame uniforms from the sky system + weather */
  update(camPos: Vec3, sky: WaterSkySource, time: number): void {
    const m = this.mat;
    const s = this.scene;
    m.setVector3('camPos', new this.B.Vector3(camPos.x, camPos.y, camPos.z));
    m.setVector3('sunDir', new this.B.Vector3(sky.sunDir.x, sky.sunDir.y, sky.sunDir.z));
    m.setColor3('sunColor', new this.B.Color3(sky.sunColor.x, sky.sunColor.y, sky.sunColor.z));
    m.setFloat('time', time);
    m.setColor3('fogColor', s.fogColor);
    m.setFloat('fogDensity', s.fogDensity);
    m.setFloat('rain', sky.rain);
    m.setFloat('night', sky.night);
    m.setFloat('overcast', sky.overcast);
  }
}
